import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { Send, Loader2, Sun, TrendingDown, CheckCircle2, MessageCircle } from "lucide-react";
import { WHATSAPP_LINK, buildWhatsAppMessage, openWhatsApp } from "@/lib/constants";

const EASE = [0.16, 1, 0.3, 1];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PRESETS = [300, 500, 1000, 2500, 5000];

const brl = (v) =>
  v.toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });

export const QuoteForm = () => {
  const [nome, setNome] = useState("");
  const [telefone, setTelefone] = useState("");
  const [cidade, setCidade] = useState("");
  const [valorConta, setValorConta] = useState(500);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [whatsMsg, setWhatsMsg] = useState("");

  const economiaMes = valorConta * 0.9;
  const economia25 = economiaMes * 12 * 25;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!nome.trim() || !telefone.trim() || !cidade.trim() || !valorConta) {
      toast.error("Preencha todos os campos para receber seu orçamento.");
      return;
    }
    const payload = {
      nome: nome.trim(),
      telefone: telefone.trim(),
      cidade: cidade.trim(),
      valor_conta: Number(valorConta),
    };
    const msg = buildWhatsAppMessage({
      nome: payload.nome,
      telefone: payload.telefone,
      cidade: payload.cidade,
      valorConta: payload.valor_conta,
    });
    setWhatsMsg(msg);
    openWhatsApp(msg);
    setSending(true);
    try {
      await axios.post(`${API}/leads`, payload);
      toast.success("Pedido registrado! Conclua o envio no WhatsApp que abriu.");
      setSent(true);
    } catch (err) {
      console.error("Falha ao enviar pedido", err);
      toast.error("Não conseguimos registrar agora, mas você pode concluir no WhatsApp.");
      setSent(true);
    }
    setSending(false);
  };

  const resetForm = () => {
    setNome("");
    setTelefone("");
    setCidade("");
    setValorConta(500);
    setSent(false);
    setWhatsMsg("");
  };

  const inputCls =
    "w-full rounded-xl border border-slate-300 bg-white px-4 py-3.5 text-base text-[#0B192C] placeholder:text-slate-400 outline-none transition-all duration-300 focus:border-amber-500 focus:ring-4 focus:ring-amber-400/20";

  return (
    <section id="orcamento" className="relative py-24 lg:py-32 bg-[#FAFAF9] overflow-hidden">
      <div className="pointer-events-none absolute -top-24 left-1/2 -translate-x-1/2 h-[300px] w-[700px] rounded-full bg-amber-300/25 blur-[120px]" />
      <div className="relative max-w-6xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: EASE }}
          className="text-center max-w-2xl mx-auto"
        >
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-amber-500 mb-4">
            Orçamento gratuito
          </p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight text-[#0B192C]">
            Descubra quanto você pode{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-500 to-amber-600">
              economizar
            </span>
          </h2>
          <p className="mt-4 text-base sm:text-lg text-slate-600">
            Preencha abaixo, envie e conclua no WhatsApp. Sem compromisso.
          </p>
        </motion.div>

        <motion.div
          data-testid="solar-calculator-container"
          initial={{ opacity: 0, y: 48 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 1, delay: 0.1, ease: EASE }}
          className="mt-14 grid lg:grid-cols-[1fr_1.1fr] rounded-[2rem] overflow-hidden border border-slate-200 shadow-[0_40px_80px_rgba(11,25,44,0.10)] bg-white"
        >
          <div className="relative grain bg-[#0B192C] p-8 sm:p-12 flex flex-col justify-between gap-10">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-amber-400">
                Simulador de economia
              </p>
              <p className="mt-6 text-sm font-semibold text-slate-300">
                Valor médio da sua conta de luz
              </p>
              <p className="font-display text-5xl sm:text-6xl font-extrabold text-white mt-2 tabular-nums">
                {brl(Number(valorConta) || 0)}
                <span className="text-lg font-bold text-slate-400">/mês</span>
              </p>
              <input
                data-testid="calculator-bill-input"
                type="range"
                min="200"
                max="8000"
                step="50"
                value={valorConta}
                onChange={(e) => setValorConta(Number(e.target.value))}
                className="mt-6 w-full accent-amber-400 cursor-pointer"
              />
              <div className="mt-4 flex flex-wrap gap-2">
                {PRESETS.map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setValorConta(p)}
                    className={`rounded-full px-4 py-1.5 text-xs font-bold transition-colors duration-300 ${
                      Number(valorConta) === p
                        ? "bg-amber-400 text-[#0B192C]"
                        : "bg-white/10 text-slate-200 hover:bg-white/20"
                    }`}
                  >
                    {brl(p)}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-4">
              <div className="flex items-center gap-4 rounded-2xl bg-white/5 border border-white/10 px-5 py-4">
                <TrendingDown className="h-8 w-8 text-amber-400 shrink-0" />
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">
                    Economia estimada por mês
                  </p>
                  <p data-testid="savings-monthly" className="font-display text-2xl font-extrabold text-amber-400 tabular-nums">
                    até {brl(economiaMes)}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4 rounded-2xl bg-amber-400/10 border border-amber-400/30 px-5 py-4">
                <Sun className="h-8 w-8 text-amber-400 shrink-0" />
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-amber-300/80">
                    Em 25 anos de geração
                  </p>
                  <p data-testid="savings-25-years" className="font-display text-2xl font-extrabold text-white tabular-nums">
                    até {brl(economia25)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="p-8 sm:p-12">
            {sent ? (
              <motion.div
                data-testid="quote-success-message"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, ease: EASE }}
                className="flex h-full flex-col items-center justify-center py-8 text-center"
              >
                <span className="flex h-20 w-20 items-center justify-center rounded-full bg-emerald-100">
                  <CheckCircle2 className="h-10 w-10 text-emerald-600" />
                </span>
                <h3 className="mt-6 font-display text-2xl font-bold tracking-tight text-[#0B192C]">
                  Pedido registrado com sucesso!
                </h3>
                <p className="mt-3 max-w-sm text-base leading-relaxed text-slate-600">
                  Seus dados já chegaram à HB Solar por e-mail. Agora toque no
                  botão verde abaixo para concluir o envio pelo seu WhatsApp.
                </p>
                <div className="mt-8 flex w-full flex-col items-center gap-3">
                  <a
                    data-testid="quote-whatsapp-fallback-link"
                    href={`${WHATSAPP_LINK}?text=${encodeURIComponent(whatsMsg)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex w-full sm:w-auto items-center justify-center gap-2.5 rounded-full bg-[#25D366] px-8 py-4 text-base font-extrabold text-white shadow-[0_16px_40px_rgba(37,211,102,0.35)] transition-all duration-300 hover:bg-[#1fb857] hover:scale-[1.02]"
                  >
                    <MessageCircle className="h-5 w-5" />
                    Concluir envio no WhatsApp
                  </a>
                  <p className="max-w-sm text-xs leading-relaxed text-slate-500 font-medium">
                    O botão verde abre o seu WhatsApp com a mensagem pronta para a HB Solar — é só tocar em enviar.
                  </p>
                  <button
                    data-testid="quote-new-request-button"
                    onClick={resetForm}
                    className="mt-2 rounded-full border border-slate-300 px-6 py-3 text-sm font-bold text-slate-600 transition-colors duration-300 hover:border-amber-400 hover:text-[#0B192C]"
                  >
                    Fazer novo orçamento
                  </button>
                </div>
              </motion.div>
            ) : (
              <form
                data-testid="quote-request-form"
                onSubmit={handleSubmit}
                className="space-y-5"
              >
                <div>
                  <label htmlFor="quote-nome" className="block text-xs font-bold uppercase tracking-[0.16em] text-slate-500 mb-2">
                    Nome completo
                  </label>
                  <input
                    id="quote-nome"
                    data-testid="quote-input-name"
                    type="text"
                    value={nome}
                    onChange={(e) => setNome(e.target.value)}
                    placeholder="Seu nome"
                    className={inputCls}
                    required
                  />
                </div>
                <div>
                  <label htmlFor="quote-telefone" className="block text-xs font-bold uppercase tracking-[0.16em] text-slate-500 mb-2">
                    Telefone / WhatsApp
                  </label>
                  <input
                    id="quote-telefone"
                    data-testid="quote-input-phone"
                    type="tel"
                    value={telefone}
                    onChange={(e) => setTelefone(e.target.value)}
                    placeholder="(73) 99999-9999"
                    className={inputCls}
                    required
                  />
                </div>
                <div>
                  <label htmlFor="quote-cidade" className="block text-xs font-bold uppercase tracking-[0.16em] text-slate-500 mb-2">
                    Cidade
                  </label>
                  <input
                    id="quote-cidade"
                    data-testid="quote-input-city"
                    type="text"
                    value={cidade}
                    onChange={(e) => setCidade(e.target.value)}
                    placeholder="Ex: Ilhéus, Itabuna, Porto Seguro..."
                    className={inputCls}
                    required
                  />
                </div>
                <div>
                  <label htmlFor="quote-valor" className="block text-xs font-bold uppercase tracking-[0.16em] text-slate-500 mb-2">
                    Valor médio da conta de luz (R$)
                  </label>
                  <input
                    id="quote-valor"
                    data-testid="quote-input-bill"
                    type="number"
                    min="50"
                    value={valorConta}
                    onChange={(e) => setValorConta(e.target.value)}
                    placeholder="Ex: 500"
                    className={inputCls}
                    required
                  />
                </div>
                <button
                  data-testid="quote-submit-whatsapp-button"
                  type="submit"
                  disabled={sending}
                  className="group w-full inline-flex items-center justify-center gap-3 rounded-full bg-[#25D366] hover:bg-[#1fb857] text-white font-extrabold text-base px-7 py-4 transition-colors duration-300 shadow-[0_16px_40px_rgba(37,211,102,0.35)] disabled:opacity-60"
                >
                  {sending ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <Send className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
                  )}
                  {sending ? "Enviando..." : "Receber orçamento no WhatsApp"}
                </button>
                <p className="text-center text-xs text-slate-400 font-medium">
                  Ao enviar, o WhatsApp abre com a mensagem pronta para a HB Solar — e seus dados também chegam por e-mail.
                </p>
              </form>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
